/* eslint-disable @typescript-eslint/ban-ts-comment */
import { useState, useCallback } from 'react';
import { read, utils } from 'xlsx';
import { createComprobante } from './accounting-usecase';
import { getAllSubContableAcounts } from './contable-acounts-usecase';
import { getThirdByIdentification } from './thirds-usecase';
import { dispatchCustomEvent } from '../utils/dispatch';
// @ts-ignore
import { dateFormat } from '../utils/helper';
import { Third } from '../app/domain/models/thirds/third';

export interface SubCuentaContable {
    id: number;
    code: number;
    description: string;
    base_account?: {
        account: string;
        description: string;
    };
}

export interface LineaContableMasiva {
    numero_linea: number;
    cuenta_contable_id: number;
    tercero_id?: number;
    tercero_object?: Third;
    centro_costo_id: number;
    valor_debito: number;
    valor_credito: number;
    descripcion: string;
    aditional_info: SubCuentaContable;
}

/**
 * Interface for the Batch Comprobante State
 */
export interface ComprobanteMasivo {
    id: string; // Unique ID for local management (random or filename)
    file_name: string;
    isValid: boolean;
    validation_errors: string[];
    cabecera: {
        descripcion: string;
        document_type: string;
        document: string;
        date: string;
    };
    lineas: LineaContableMasiva[];
    total_debito: number;
    total_credito: number;
    status: 'pending' | 'loading' | 'success' | 'error';
    error_message?: string;
}

export function useCargaMasiva() {
    const [comprobantes, setComprobantes] = useState<ComprobanteMasivo[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({ total: 0, current: 0 });
    const [parseProgress, setParseProgress] = useState({ isParsing: false, total: 0, current: 0, currentFileName: '' });

    /**
     * Parses a single Excel file into a ComprobanteMasivo object
     * Format: 
     * Row 2 (index 1): Cabecera [Desc, Tipo, Doc, Fecha]
     * Row 4+ (index 3+): Lineas [Cuenta, Tercero, Debito, Credito, Detalle]
     */
    const parseExcelToComprobante = async (file: File, allAccounts: SubCuentaContable[]): Promise<ComprobanteMasivo> => {
        const errors: string[] = [];
        try {
            const data = await file.arrayBuffer();
            const workbook = read(data);
            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            // Read as matrix to have precise row control
            const rows: any[][] = utils.sheet_to_json(worksheet, { header: 1 });

            if (rows.length < 2) throw new Error("Archivo vacío o formato inválido");

            // Cabecera (Row 2 -> index 1)
            const headerRow = rows[1] || [];
            
            let parsedDate = "";
            const rawDate = headerRow[3];
            
            if (rawDate) {
                if (rawDate instanceof Date) {
                    // Si ya es un objeto Date de xlsx, extraemos componentes locales
                    parsedDate = `${rawDate.getFullYear()}-${rawDate.getMonth() + 1}-${rawDate.getDate()}`;
                } else {
                    const dateStr = rawDate.toString().trim();
                    // Si tiene formato YYYY-MM-DD, evitamos el shift de timezone de new Date()
                    if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
                        parsedDate = dateStr.split(' ')[0];
                    } else {
                        // Reemplazar - por / para forzar interpretación local en el navegador
                        parsedDate = dateFormat(new Date(dateStr.replace(/-/g, '/')));
                    }
                }
            } else {
                parsedDate = dateFormat(new Date());
            }

            const cabecera = {
                descripcion: headerRow[0]?.toString() || file.name.replace(/\.[^/.]+$/, ""),
                document_type: headerRow[1]?.toString() || "",
                document: headerRow[2]?.toString() || "",
                date: parsedDate
            };

            if (!cabecera.document_type) errors.push("Tipo de documento faltante");

            const lineas: LineaContableMasiva[] = [];
            let totalDebito = 0;
            let totalCredito = 0;

            // Lineas (Row 4+ -> index 3+)
            for (let i = 3; i < rows.length; i++) {
                const row = rows[i];
                if (!row || row.length === 0 || !row[0]) continue;

                const accountCode = row[0].toString().trim();
                
                // Skip header row if it is accidentally caught
                if (accountCode.toUpperCase().includes('CUENTA') || accountCode.toUpperCase().includes('CÓDIGO')) {
                    continue;
                }

                const account = allAccounts.find(acc => {
                    const paddedCode = String(acc.code ?? '').padStart(2, '0');
                    const baseAccount = acc.base_account?.account || '';
                    const fullAccount = baseAccount ? `${baseAccount}${paddedCode}` : paddedCode;
                    return fullAccount === accountCode;
                });

                if (!account) {
                    errors.push(`Cuenta ${accountCode} no encontrada en el sistema`);
                    continue;
                }

                let thirdId = undefined;
                let thirdObj = undefined;
                const thirdRef = row[1]?.toString();

                if (thirdRef) {
                    try {
                        const foundThird = await getThirdByIdentification(thirdRef);
                        if (foundThird) {
                            thirdId = foundThird.id;
                            thirdObj = foundThird;
                        } else {
                            errors.push(`Tercero ${thirdRef} no encontrado`);
                        }
                    } catch (err) {
                        console.error(`Error searching third ${thirdRef}:`, err);
                        errors.push(`Error técnico al buscar tercero ${thirdRef}: Fallo de conexión o respuesta del servidor`);
                    }
                }

                const debito = parseFloat(row[2] || 0);
                const credito = parseFloat(row[3] || 0);
                totalDebito += debito;
                totalCredito += credito;

                lineas.push({
                    numero_linea: lineas.length + 1,
                    cuenta_contable_id: account.id,
                    tercero_id: thirdId,
                    tercero_object: thirdObj,
                    centro_costo_id: 1,
                    valor_debito: debito,
                    valor_credito: credito,
                    descripcion: row[4] || account.description,
                    aditional_info: account
                });
            }

            if (lineas.length < 2) errors.push("Un comprobante debe tener al menos 2 líneas");
            if (Math.abs(totalDebito - totalCredito) > 0.01) errors.push("Comprobante fuera de balance");

            return {
                id: Math.random().toString(36).substr(2, 9),
                file_name: file.name,
                isValid: errors.length === 0,
                validation_errors: errors,
                cabecera,
                lineas,
                total_debito: totalDebito,
                total_credito: totalCredito,
                status: 'pending'
            };

        } catch (e: any) {
            return {
                id: Math.random().toString(36).substr(2, 9),
                file_name: file.name,
                isValid: false,
                validation_errors: [e.message || "Error desconocido al procesar archivo"],
                cabecera: { descripcion: "", document_type: "", document: "", date: "" },
                lineas: [],
                total_debito: 0,
                total_credito: 0,
                status: 'pending'
            };
        }
    };

    /**
     * Processes a list of files from a directory
     */
    const processFiles = useCallback(async (files: FileList | File[]) => {
        setIsProcessing(true);
        try {
            const allAccounts = await getAllSubContableAcounts("", "");
            console.log("Files received from input:", Array.from(files).map(f => f.name));

            const fileArray = Array.from(files).filter(f => {
                const lowerName = f.name.toLowerCase();
                const isExcel = lowerName.endsWith('.xlsx') || lowerName.endsWith('.xls');
                const isHidden = f.name.startsWith('._') || f.name.startsWith('.');
                return isExcel && !isHidden;
            });
            console.log("Filtered Excel files:", fileArray.map(f => f.name));

            if (fileArray.length === 0) {
                dispatchCustomEvent('popup', { message: "No se encontraron archivos Excel válidos en la carpeta seleccionada.", type: "warning" });
                setIsProcessing(false);
                return;
            }

            setParseProgress({ isParsing: true, total: fileArray.length, current: 0, currentFileName: '' });

            const results: ComprobanteMasivo[] = [];
            for (let i = 0; i < fileArray.length; i++) {
                const file = fileArray[i];
                setParseProgress(prev => ({ ...prev, current: i + 1, currentFileName: file.name }));
                
                const result = await parseExcelToComprobante(file, allAccounts);
                results.push(result);
                
                // Allow UI to update
                await new Promise(resolve => setTimeout(resolve, 10));
            }

            setComprobantes(prev => [...prev, ...results]);
        } catch (error) {
            console.error("Error processing files:", error);
            dispatchCustomEvent('popup', { message: "Error al procesar la carpeta", type: "error" });
        } finally {
            setIsProcessing(false);
            setParseProgress({ isParsing: false, total: 0, current: 0, currentFileName: '' });
        }
    }, []);

    /**
     * Removes a comprobante from the local state
     */
    const removeComprobante = (id: string) => {
        setComprobantes(prev => prev.filter(c => c.id !== id));
    };

    /**
     * Uploads all pending and valid comprobantes to the backend sequentially
     */
    const uploadAll = async () => {
        const toUpload = comprobantes.filter(c => c.isValid && (c.status === 'pending' || c.status === 'error'));
        if (toUpload.length === 0) return;

        setIsProcessing(true);
        setUploadProgress({ total: toUpload.length, current: 0 });

        let successCount = 0;
        let failCount = 0;

        for (const item of toUpload) {
            // Update item status to loading
            setComprobantes(prev => prev.map(c => c.id === item.id ? { ...c, status: 'loading' } : c));

            try {
                const payload = {
                    ...item.cabecera,
                    lineas: item.lineas
                };
                await createComprobante(payload);

                setComprobantes(prev => prev.map(c => c.id === item.id ? { ...c, status: 'success' } : c));
                successCount++;
            } catch (error: any) {
                console.error(`Error uploading ${item.file_name}:`, error);
                const errMsg = error.response?.data?.message || error.response?.data?.error?.message || error.message || "Error de red";
                setComprobantes(prev => prev.map(c => c.id === item.id ? { ...c, status: 'error', error_message: errMsg } : c));
                failCount++;
            } finally {
                setUploadProgress(prev => ({ ...prev, current: prev.current + 1 }));
            }
        }

        setIsProcessing(false);
        dispatchCustomEvent('loading-btn', {});
        
        dispatchCustomEvent('popup', {
            message: `Proceso terminado. ${successCount} exitosos, ${failCount} fallidos.`,
            type: failCount > 0 ? "warning" : "success"
        });

        if (successCount > 0) {
            dispatchCustomEvent('reload-comprobantes', {});
        }
    };

    const clear = () => {
        setComprobantes([]);
        setUploadProgress({ total: 0, current: 0 });
    };

    return {
        comprobantes,
        isProcessing,
        uploadProgress,
        parseProgress,
        processFiles,
        removeComprobante,
        uploadAll,
        clear
    };
}
